import React, { useContext, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Navbar } from '../components'
import { AbilityContext } from '../shared/abilities/AbilityContext'
import * as constant from '../shared/util/constant'

/**
 * In-page tab strip for /accounting.
 *
 * Mirrors `src/Campaign/campaignNavbar.tsx` per Krishna's direction
 * ("take the campaign reference"). Reuses the shared `Navbar` from
 * `src/components/components/Navbar/Navbar.tsx` — no custom navbar UI.
 *
 * Path-to-tab map drives active highlighting via `.nav-link.active`,
 * matching the same CSS hook campaignNavbar relies on.
 */
const TAB_FOR_PATH: Record<string, string> = {
  '/accounting/Reconciliation': 'Reconciliation',
  '/accounting/Import': 'Other',
  '/accounting/CompensationRate': 'CompensationRate',
}

const AccountingNavbar = () => {
  const history = useHistory()
  const location = useLocation()
  const ability = useContext(AbilityContext)

  const canViewReconcile = ability.can('view', constant.AccountingSubject, constant.AccountingScreenPrivileges.Reconcile)
  const canReadReconcile = ability.can('read', constant.AccountingSubject, constant.AccountingScreenPrivileges.Reconcile)
  const canViewCompensationRate = ability.can('view', constant.AccountingSubject, constant.AccountingScreenPrivileges.Compensation_Rate)
  const canReadCompensationRate = ability.can('read', constant.AccountingSubject, constant.AccountingScreenPrivileges.Compensation_Rate)
  const canViewHistory = ability.can('view', constant.AccountingSubject, constant.AccountingScreenPrivileges.History)
  const canReadHistory = ability.can('read', constant.AccountingSubject, constant.AccountingScreenPrivileges.History)

  useEffect(() => {
    // Re-highlight the active tab whenever the URL changes (including dropdown navigation).
    const tabLinks = document.querySelectorAll('.nav-link')
    tabLinks.forEach((item) => item.classList.remove('active'))

    const activeTab = TAB_FOR_PATH[location.pathname]
    if (!activeTab) return

    tabLinks.forEach((item) => {
      // Top-level Nav.Link: matched by the id our navItem set (id={link.name} in shared Navbar).
      if (item.id === activeTab) {
        item.classList.add('active')
      }
      // NavDropdown toggle: shared Navbar hardcodes `id="collasible-nav-dropdown"` on every
      // dropdown, so we cannot match the "Other" toggle by id. We disambiguate by visible
      // text (which is the dropdown's `title` prop and uniquely "Other" here). Documented
      // because pattern is unusual; revisit if the shared Navbar exposes per-dropdown ids.
      if (
        activeTab === 'Other' &&
        item.classList.contains('dropdown-toggle') &&
        item.textContent?.trim().startsWith('Other')
      ) {
        item.classList.add('active')
      }
    })
  }, [location.pathname])

  return (
    <Navbar
      bg="light"
      variant="light"
      className="pb-2"
      title={''}
      id={''}
      navItems={[
        {
          type: 'header',
          alignRight: true,
          children: null,
          className:
            'cstHeaderMenu nav-add-new d-none d-md-block nav-bar-item-width cst-header-menu-default',
          iconClassName: 'align-bottom',
          label: 'Accounting',
          name: 'AccountingHeader',
          size: 'sm',
        },
        ...(canViewReconcile ? [{
          type: 'link',
          alignRight: true,
          children: null,
          className: `cstHeaderMenu nav-add-new d-none d-md-block nav-bar-item-width${!canReadReconcile ? ' disabled' : ''}`,
          iconClassName: 'align-bottom',
          label: 'Reconciliation',
          name: 'Reconciliation',
          size: 'sm',
          disabled: !canReadReconcile,
          onClick: canReadReconcile ? () => history.push('/accounting/Reconciliation') : undefined,
        }] : []),
        {
          type: 'link-list',
          alignRight: true,
          className: 'cstHeaderMenu nav-add-new d-none d-md-block nav-bar-item-width',
          label: 'Other',
          name: 'Other',
          children: [
            {
              type: 'link',
              label: 'Import Commission Statement',
              name: 'Import',
              onClick: () => history.push('/accounting/Import'),
            },
          ],
        },
        ...(canViewCompensationRate ? [{
          type: 'link',
          alignRight: true,
          children: null,
          className: `cstHeaderMenu nav-add-new d-none d-md-block nav-bar-item-width${!canReadCompensationRate ? ' disabled' : ''}`,
          iconClassName: 'align-bottom',
          label: 'Compensation Rate',
          name: 'CompensationRate',
          size: 'sm',
          disabled: !canReadCompensationRate,
          onClick: canReadCompensationRate ? () => history.push('/accounting/CompensationRate') : undefined,
        }] : []),
      ]}
      children={''}
    />
  )
}

export default AccountingNavbar
